import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "@/components/icons";
import { fetchAuthProviders } from "@/lib/api";
import { request, responseError } from "@/lib/http";
import { useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

// The ?error=... codes the Google callback appends when it redirects back to
// /login on failure; any other code gets the generic sentence.
const GOOGLE_ERROR_CODES = new Set([
  "google_disabled",
  "google_denied",
  "google_state",
  "google_token",
  "google_aud",
  "google_iss",
  "google_expired",
  "google_email",
  "google_account",
  "account_disabled",
  "session",
]);

export function LoginPage() {
  const t = useT();
  const nav = useNavigate();
  const location = useLocation() as any;
  const from = location.state?.from?.pathname || "/";

  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  // Which external providers to offer (public, pre-session).
  const providersQ = useQuery({ queryKey: ["auth-providers"], queryFn: fetchAuthProviders });
  const googleEnabled = providersQ.data?.google_login ?? false;
  const registrationEnabled = providersQ.data?.phone_registration ?? false;

  // Surface an error code the Google callback may have appended to the URL.
  React.useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("error");
    if (code) {
      toast.error(t("operator.auth.google_error", { code: GOOGLE_ERROR_CODES.has(code) ? code : "other" }));
      window.history.replaceState({}, "", "/login");
    }
  }, [t]);

  function signInWithGoogle() {
    // Full-page navigation: the backend redirects to Google and back.
    const next = encodeURIComponent(from);
    window.location.href = `/auth/google/login?next=${next}`;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const body = new URLSearchParams();
      body.set("username", username);
      body.set("password", password);

      const res = await request("/login", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
        credentials: "include",
        redirect: "follow",
      });

      // The form login answers HTML; its body (a proxy or error page) is never shown.
      if (!res.ok) throw await responseError(res);

      toast.success(t("operator.auth.welcome_back"));
      nav(from, { replace: true });
    } catch (err) {
      toast.error(errorMessage(t, err, { fallback: "operator.auth.login_failed" }));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-12 text-zinc-950 dark:bg-black dark:text-zinc-50">
      <div className="mx-auto w-full max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>{t("operator.auth.sign_in_title")}</CardTitle>
            <CardDescription>{t("operator.auth.sign_in_description")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label>{t("operator.auth.username")}</Label>
                <Input className="mt-2" value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
              </div>
              <div>
                <Label>{t("operator.auth.password")}</Label>
                <div className="relative mt-2">
                  <Input className="pr-10" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} />
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label={showPassword ? t("operator.auth.hide_password") : t("operator.auth.show_password")}
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button className="w-full" type="submit" disabled={loading || !username || !password}>
                {loading ? t("operator.auth.signing_in") : t("operator.auth.sign_in")}
              </Button>
              <div className="flex items-center justify-center gap-3 text-center">
                <Link
                  to="/forgot-password"
                  className="text-xs text-zinc-500 underline hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                >
                  {t("operator.auth.forgot_password_link")}
                </Link>
                {registrationEnabled && (
                  <>
                    <span className="text-xs text-zinc-300 dark:text-zinc-600">·</span>
                    <Link
                      to="/register"
                      className="text-xs text-zinc-500 underline hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                    >
                      {t("operator.auth.register_link")}
                    </Link>
                  </>
                )}
              </div>
            </form>

            {googleEnabled && (
              <>
                <div className="my-4 flex items-center gap-3 text-xs text-zinc-400">
                  <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
                  {t("operator.auth.or_divider")}
                  <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={signInWithGoogle}
                >
                  {t("operator.auth.sign_in_with_google")}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-xs text-zinc-500 dark:text-zinc-400">
          {t("operator.auth.session_expired_hint")}
        </div>
      </div>
    </div>
  );
}
